import { Tooltip, TooltipContent, TooltipTrigger } from "@/kit/ui/tooltip";
import { useMemo } from "react";
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  useIntl,
} from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { cn } from "../utils/tailwind";
import {
  TimelineFragment$data,
  TimelineFragment$key,
} from "./__generated__/TimelineFragment.graphql";
import { RelativeTime } from "./RelativeTime";
import { ZonedTime } from "./ZonedTime";

const TimelineFragmentDef = graphql`
  fragment TimelineFragment on Timeline {
    startDate
    endDate
    timezone
    online
    steps {
      edges {
        node {
          label
          date
        }
      }
    }
  }
`;

interface Props {
  timeline: TimelineFragment$key;
}

interface StepGroup {
  date: Date;
  labels: string[];
}

function toDayKey(date: Date): string {
  return date.toISOString().split("T")[0];
}

function groupStepsByDay(
  edges: TimelineFragment$data["steps"]["edges"],
): ReadonlyArray<StepGroup> {
  const groups = new Map<string, StepGroup>();
  for (const { node } of edges) {
    const date = new Date(node.date);
    const key = toDayKey(date);
    const existing = groups.get(key);
    if (existing) {
      existing.labels.push(node.label);
    } else {
      groups.set(key, { date, labels: [node.label] });
    }
  }
  return Array.from(groups.values());
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function getPositionPercent(
  date: Date,
  start: Date,
  durationMs: number,
): number {
  return durationMs
    ? ((date.getTime() - start.getTime()) / durationMs) * 100
    : 0;
}

export function Timeline({ timeline: timelineFragment }: Props) {
  const intl = useIntl();
  const timeline = useFragment(TimelineFragmentDef, timelineFragment);

  const startDate = new Date(timeline.startDate);
  const endDate = new Date(timeline.endDate);

  const totalDuration = endDate.getTime() - startDate.getTime();
  const elapsedMs = clamp(Date.now() - startDate.getTime(), 0, totalDuration);
  const todayPosition = totalDuration ? (elapsedMs / totalDuration) * 100 : 0;

  const groupedSteps = useMemo(
    () => groupStepsByDay(timeline.steps.edges),
    [timeline.steps.edges],
  );

  return (
    <div className="w-full mb-12">
      <div className="flex justify-between">
        <div className="flex flex-col gap-1">
          <p className="font-bold text-lg leading-6">
            <FormattedMessage defaultMessage="Start" />
          </p>
          <p className="text-sm font-thin text-grey-500">
            {timeline.online ? (
              <ZonedTime date={startDate} timezone={timeline.timezone} />
            ) : (
              <RelativeTime date={startDate} />
            )}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <p className="font-bold text-lg leading-6">
            <FormattedMessage defaultMessage="End" />
          </p>
          <p className="text-sm font-thin text-grey-500">
            {timeline.online ? (
              <ZonedTime date={endDate} timezone={timeline.timezone} />
            ) : (
              <RelativeTime date={endDate} />
            )}
          </p>
        </div>
      </div>
      <section
        aria-label={intl.formatMessage({ defaultMessage: "Timeline" })}
        role="group"
        className="relative py-8"
      >
        <div className="relative" role="presentation">
          <Line position={todayPosition} aria-hidden="true" />
          <div
            className="absolute"
            style={{ left: "0%", transform: "translate(-10%, -40%)" }}
          >
            <DateToolTip
              date={startDate}
              timezone={timeline.online ? timeline.timezone : undefined}
              className="bg-black"
              tooltipId="tooltip-start"
            />
          </div>
          <ol className="relative" role="list">
            {groupedSteps.map(({ date, labels }) => {
              const key = toDayKey(date);
              const stepPosition = getPositionPercent(
                date,
                startDate,
                totalDuration,
              );
              const stepCount = labels.length;
              return (
                <li
                  key={key}
                  className="absolute list-none gap-1"
                  style={{
                    left: `${stepPosition}%`,
                    transform: "translate(-50%, -40%)",
                  }}
                  role="listitem"
                >
                  <DateToolTip
                    date={date}
                    timezone={timeline.online ? timeline.timezone : undefined}
                    className="border-2 border-black bg-white"
                    count={stepCount}
                    caption={stepCount === 1 ? labels[0] : undefined}
                    labels={labels}
                    tooltipId={`tooltip-step-${key}`}
                  />
                </li>
              );
            })}
            <li
              className="absolute"
              style={{
                left: `${todayPosition}%`,
                transform: "translate(-50%, -50%)",
              }}
              role="presentation"
              title={intl.formatMessage({ defaultMessage: "Today" })}
            >
              <Point className="bg-brand-500" aria-hidden="true" />
            </li>
          </ol>
          <div
            className="absolute"
            style={{ left: "100%", transform: "translate(-50%, -40%)" }}
          >
            <DateToolTip
              date={endDate}
              timezone={timeline.online ? timeline.timezone : undefined}
              className="bg-black"
              tooltipId="tooltip-end"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

interface DateToolTipProps {
  date: Date;
  tooltipId: string;
  count?: number;
  timezone?: string;
  className?: string;
  caption?: string;
  labels?: ReadonlyArray<string>;
}

function DateToolTip({
  date,
  tooltipId,
  count,
  className,
  caption,
  timezone,
  labels,
}: Readonly<DateToolTipProps>) {
  return (
    <Tooltip>
      <div className="z-10 relative">
        <TooltipTrigger asChild>
          <button
            type="button"
            className="relative w-full focus:outline-solid focus:outline-2 focus:outline-offset-2"
            aria-describedby={tooltipId}
          >
            <Point className={className} count={count} />
            {caption && (
              <span className="absolute top-6 transform -translate-x-1/2 text-xs text-grey-600">
                {caption}
              </span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <time
            id={tooltipId}
            dateTime={date.toISOString()}
            className="mt-2 text-sm text-grey-600"
          >
            <FormattedDate
              value={date}
              timeZone={timezone}
              localeMatcher={timezone ? undefined : "best fit"}
              year="numeric"
              month="long"
              day="numeric"
              timeZoneName="shortGeneric"
            />
          </time>
          {labels && labels.length > 0 && (
            <ul className="mt-1 flex flex-col gap-0.5">
              {labels.map((text, index) => (
                <li key={index} className="list-none text-sm text-grey-900">
                  {text}
                </li>
              ))}
            </ul>
          )}
        </TooltipContent>
      </div>
    </Tooltip>
  );
}

function Line({
  position,
  ...rest
}: { position: number } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className="absolute top-1/2 transform -translate-y-1/2 inset-x-0 flex"
      aria-hidden="true"
      {...rest}
    >
      <div
        style={{ width: `${position}%` }}
        className="border-t border-solid border-grey-700"
      />
      <div
        style={{ width: `${100 - position}%` }}
        className="border-t border-dashed border-grey-700"
      />
    </div>
  );
}

interface PointProps extends React.HTMLAttributes<HTMLDivElement> {
  readonly count?: number;
}

function Point({ className, count, ...rest }: PointProps) {
  return (
    <div
      className={cn(
        "grid h-4 w-4 shrink-0 place-items-center rounded-full",
        className,
        count && "mb-3",
      )}
      aria-hidden="true"
      {...rest}
    >
      {count !== undefined && (
        <span className="font-semibold leading-none tabular-nums text-[10px]">
          <FormattedNumber
            value={count}
            notation="compact"
            maximumFractionDigits={2}
          />
        </span>
      )}
    </div>
  );
}
